import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const VIEWPORT_START_OFFSET = 50;
const COPY_TEXT_RESET_DELAY = 3000;

/** Keep `start` in sync with scrollStartAlreadyMet() or visible content can sit between the two thresholds (desktop). */
function scrollTriggerDefaults() {
	return {
		start: `top bottom-=${VIEWPORT_START_OFFSET}px`,
		once: true,
		invalidateOnRefresh: true,
	};
}
const IN_VIEW_LOAD_DELAY = 1.5;

/** True when `el` has already passed the ScrollTrigger start line (would fire immediately on load). */
function scrollStartAlreadyMet(el) {
	const line = window.innerHeight - VIEWPORT_START_OFFSET;
	return el.getBoundingClientRect().top <= line;
}

/** ScrollTrigger ignores `delay` on tweens; use timeline start position instead. */
function entrancePosition(triggerEl) {
	return scrollStartAlreadyMet(triggerEl) ? IN_VIEW_LOAD_DELAY : 0;
}

export default class BlockCTA {
	constructor(block) {
		this.block = block;
		this.title = this.block.querySelector(".block-title");
		this.text = this.block.querySelector(".block-intro-text");
		this.textBlocks = this.text ? Array.from(this.text.children) : [];
		this.form = this.block.querySelector("form");
		this.copyLinkButtons = Array.from(this.block.querySelectorAll(".copy-link"));
		this.contactModal = document.getElementById("getInTouchModal");
		this.contactFrame = document.getElementById("WFM_29224_1");
		this.contactFormUrl = "";
		this.hasResizerInit = false;

		this.setStartingPositions();
		this.animateIn();
		this.initCopyLinkButtons();
		this.initContactModal();
	}

	copyText(value) {
		if (navigator.clipboard && navigator.clipboard.writeText) {
			return navigator.clipboard.writeText(value);
		}

		return new Promise((resolve, reject) => {
			const textArea = document.createElement("textarea");
			textArea.value = value;
			textArea.setAttribute("readonly", "");
			textArea.style.position = "absolute";
			textArea.style.left = "-9999px";
			document.body.appendChild(textArea);
			textArea.select();

			try {
				const copied = document.execCommand("copy");
				document.body.removeChild(textArea);
				if (!copied) {
					reject(new Error("Copy command was not successful"));
					return;
				}
				resolve();
			} catch (error) {
				document.body.removeChild(textArea);
				reject(error);
			}
		});
	}

	initCopyLinkButtons() {
		if (!this.copyLinkButtons.length) {
			return;
		}

		this.copyLinkButtons.forEach((button) => {
			button.addEventListener("click", async (event) => {
				event.preventDefault();
				const link = button.dataset.link;
				if (!link || button.dataset.copyBusy === "true") {
					return;
				}

				try {
					button.dataset.copyBusy = "true";
					await this.copyText(link);
					button.textContent = "Copied!";
					window.setTimeout(() => {
						button.textContent = "Copy link";
						button.dataset.copyBusy = "false";
					}, COPY_TEXT_RESET_DELAY);
				} catch (error) {
					button.dataset.copyBusy = "false";
				}
			});
		});
	}

	initContactModal() {
		if (!this.contactModal || !this.contactFrame) {
			return;
		}

		this.contactModal.addEventListener("show.bs.modal", (event) => {
			const triggerButton = event.relatedTarget;
			let formUrl = triggerButton?.dataset?.contactFormUrl || this.contactFormUrl;

			if (typeof window.wfmUtmExtract === "function") {
				const extra = window.wfmUtmExtract(window.location.search);
				if (extra && extra.length) {
					formUrl += `?${extra}`;
				}
			}

			this.contactFrame.setAttribute("src", formUrl);

			if (!this.hasResizerInit && typeof window.iFrameResize === "function") {
				window.iFrameResize({ checkOrigin: false }, this.contactFrame);
				this.hasResizerInit = true;
			}
		});
	}

	setStartingPositions() {
		if (this.title) {
			gsap.set(this.title, { opacity: 0, x: -64 });
		}
		if (this.textBlocks.length) {
			gsap.set(this.textBlocks, { opacity: 0, y: 64 });
		}
		if (this.form) {
			gsap.set(this.form, { opacity: 0, y: 64 });
		}
	}

	animateIn() {
		if (this.title) {
			const pos = entrancePosition(this.title);
			const tl = gsap.timeline({
				scrollTrigger: {
					trigger: this.title,
					start: scrollTriggerDefaults().start,
					once: true,
					invalidateOnRefresh: true,
				},
			});
			tl.to(
				this.title,
				{
					opacity: 1,
					x: 0,
					duration: 0.5,
					ease: "power2.out",
				},
				pos
			);
		}

		if (this.text && this.textBlocks.length) {
			const pos = entrancePosition(this.text);
			gsap
				.timeline({
					scrollTrigger: {
						trigger: this.text,
						start: scrollTriggerDefaults().start,
					once: true,
					invalidateOnRefresh: true,
					},
				})
				.to(
					this.textBlocks,
					{
						opacity: 1,
						y: 0,
						duration: 0.75,
						stagger: 0.08,
						ease: "power2.out",
					},
					pos
				);
		}

		if (this.form) {
			const pos = entrancePosition(this.form);
			gsap.timeline({
				scrollTrigger: {
					trigger: this.form,
					start: scrollTriggerDefaults().start,
					once: true,
					invalidateOnRefresh: true,
				},
			}).to(this.form, {
				opacity: 1,
				y: 0,
				duration: 0.5,
				ease: "power2.out",
			}, pos);
		}
	}
}
